import { Checkbox, message, Tooltip } from "antd";
import { useState } from "react";
import styled from "styled-components";
import {
  AttendanceFieldsFragment,
  RollFormat,
  Week,
  WeekRollFieldsFragment,
} from "../../../../../generated/graphql";
import { InitiatedContext } from "../../../ClassAttendanceCard";
import {
  getParts,
  getTempAttendanceDataDisplayValue,
} from "../AttendanceField/attendanceData";
import AttendanceDetailsPopover from "./AttendanceDetailsPopover";
import RollCommentPopover from "./RollCommentPopover";
import {
  formatSession,
  formatSessionShortDate,
  getRollEntry,
  isFutureSession,
  isInvalidPresentComment,
} from "./rollUtils";
import useRollMutations from "./useRollMutations";

const SessionRow = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const SmallText = styled.div`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  white-space: normal;
`;

const getVisitedText = (
  tempAttendances: AttendanceFieldsFragment["tempAttendances"],
  week: Week,
  classDuration: number
): string => {
  try {
    return getTempAttendanceDataDisplayValue(
      tempAttendances,
      week,
      classDuration
    );
  } catch {
    // Visited classes with durations which aren't multiples of 30 minutes can't be adjusted.
    return "[Visited other class]";
  }
};

/**
 * Text shown for attendance recorded in the old (legacy) format, e.g. "23/04 (Comment)".
 */
const getLegacyDisplayValue = (
  attendanceData: string,
  visitedText: string
): string => {
  const [dateStr, comment] = attendanceData
    .split("|")
    .map((part) => part.trim());
  let displayValue =
    dateStr && visitedText
      ? `${dateStr}, ${visitedText}`
      : dateStr + visitedText;
  if (comment) {
    displayValue = displayValue ? `${displayValue} (${comment})` : comment;
  }
  return displayValue || "–";
};

/**
 * A student's attendance in a week: a tick for each session of the class that week.
 */
const RollCell: React.FC<{
  classId: number;
  attendanceId: number;
  week: Week;
  weekRoll?: WeekRollFieldsFragment;
  attendanceData: string;
  tempAttendances: AttendanceFieldsFragment["tempAttendances"];
  classDuration: number;
  disabledMsg?: string;
  initiatedContext: InitiatedContext;
  depth: number;
}> = ({
  classId,
  attendanceId,
  week,
  weekRoll,
  attendanceData,
  tempAttendances,
  classDuration,
  disabledMsg,
  initiatedContext,
  depth,
}) => {
  const { setRollEntries } = useRollMutations();
  // Ticks which are being saved, shown straight away.
  const [pendingTicks, setPendingTicks] = useState<Record<string, boolean>>({});
  const isDisabled = disabledMsg !== undefined;
  const visitedText = getVisitedText(tempAttendances, week, classDuration);

  if (!weekRoll || weekRoll.format === RollFormat.Legacy) {
    return (
      <AttendanceDetailsPopover
        week={week}
        tempAttendances={tempAttendances}
        legacyAttendanceData={
          getParts(attendanceData).attendanceData.length > 0 ||
          attendanceData.includes("|")
            ? attendanceData
            : undefined
        }
        legacyHint={
          isDisabled
            ? undefined
            : "To make changes, switch this week to the new roll using the button in the week's heading on the class's attendance page."
        }
        initiatedContext={initiatedContext}
        depth={depth}
      >
        {getLegacyDisplayValue(attendanceData, visitedText)}
      </AttendanceDetailsPopover>
    );
  }

  const visitedClasses = visitedText && (
    <AttendanceDetailsPopover
      week={week}
      tempAttendances={tempAttendances}
      initiatedContext={initiatedContext}
      depth={depth}
    >
      <SmallText>{visitedText}</SmallText>
    </AttendanceDetailsPopover>
  );

  if (
    weekRoll.sessions.length === 0 ||
    (isDisabled && weekRoll.format === RollFormat.LegacyEmpty)
  ) {
    return (
      <>
        {weekRoll.format === RollFormat.Sessions ? (
          <SmallText>No class</SmallText>
        ) : (
          "–"
        )}
        {visitedClasses}
      </>
    );
  }

  const save = async (
    sessionKey: string,
    isPresent: boolean,
    comment: string
  ): Promise<void> => {
    await setRollEntries({
      classId,
      entries: [{ attendanceId, comment, isPresent }],
      sessionKey,
      week,
    });
    message.success({ content: "Automatically saved", key: "roll-saved" });
  };

  const onTick = async (sessionKey: string, isPresent: boolean) => {
    const entry = getRollEntry(weekRoll, sessionKey, attendanceId);
    let comment = entry?.comment ?? "";
    if (isPresent && isInvalidPresentComment(comment)) {
      // The student can't be both present and absent.
      comment = "";
    }

    setPendingTicks((ticks) => ({ ...ticks, [sessionKey]: isPresent }));
    try {
      await save(sessionKey, isPresent, comment);
    } catch (error) {
      message.error(`Automatic save failed: ${error.message}`, 10);
    } finally {
      setPendingTicks((ticks) => {
        const newTicks = { ...ticks };
        delete newTicks[sessionKey];
        return newTicks;
      });
    }
  };

  return (
    <>
      {weekRoll.sessions.map((session) => {
        const entry = getRollEntry(weekRoll, session.key, attendanceId);
        const isPending = pendingTicks[session.key] !== undefined;
        const isFuture = isFutureSession(session);
        const tooltip =
          disabledMsg ??
          (isFuture
            ? "This session hasn't happened yet."
            : formatSession(session));

        return (
          <div key={session.key}>
            <SessionRow>
              <Tooltip title={tooltip}>
                <Checkbox
                  checked={
                    isPending
                      ? pendingTicks[session.key]
                      : entry?.isPresent ?? false
                  }
                  disabled={isDisabled || isPending || isFuture}
                  onChange={(e) => onTick(session.key, e.target.checked)}
                >
                  {formatSessionShortDate(session)}
                </Checkbox>
              </Tooltip>
              <RollCommentPopover
                comment={entry?.comment ?? ""}
                isPresent={entry?.isPresent ?? false}
                isDisabled={isDisabled || isPending}
                onSave={(comment, isPresent) =>
                  save(session.key, isPresent, comment)
                }
              />
            </SessionRow>
            {entry?.comment && <SmallText>{entry.comment}</SmallText>}
          </div>
        );
      })}
      {visitedClasses}
    </>
  );
};

export default RollCell;
